import React from 'react';
import { cn } from '../../../utils/cn';
import { inrCompact } from '../constants';
import { AuditFlag, Forensic, InvoiceRecord, RemitLine } from '../types';

/**
 * Extended dossier sections for full HTML parity:
 *  - AuditFlagBanner: where the Pro audit corrects/misclassifies the structured dashboard
 *  - ForensicPanel: negotiation tactics + adjudicated claims + auditor narrative
 *  - RemittanceTable: the client's payment-advice lines reconciled against Zoho
 */

const SEV_CLASS: Record<string, string> = {
	high: 'border-l-red-500 bg-red-50',
	medium: 'border-l-amber-500 bg-amber-50',
	low: 'border-l-gray-300 bg-gray-50',
};

const CLAIM_STATUS_CLASS: Record<string, string> = {
	corroborated: 'bg-green-100 text-green-700',
	contradicted: 'bg-red-100 text-red-700',
	uncorroborated: 'bg-amber-100 text-amber-700',
};

export const AuditFlagBanner: React.FC<{ flag?: AuditFlag }> = ({ flag }) => {
	if (!flag) return null;
	const isMis = flag.kind === 'misclassified';

	// Plain one-line verdict. Prefer the backend's verdict_plain; else derive a
	// short status from the kind (never the run-on detail sentence).
	const verdict =
		flag.verdict_plain ||
		(isMis ? 'Not about this invoice' : 'Payment cannot be verified');

	// Reason bullets. Prefer the backend's structured reasons[]; until that field
	// ships, fall back to the single detail sentence as one bullet.
	const reasons = flag.reasons && flag.reasons.length ? flag.reasons : flag.detail ? [flag.detail] : [];

	return (
		<div
			className={cn(
				'rounded-xl border p-4',
				isMis ? 'border-purple-200 bg-purple-50/70' : 'border-red-200 bg-red-50/70'
			)}
		>
			{/* Verdict — flat, one line */}
			<div className='flex items-center gap-2'>
				<span className='text-base'>⚠</span>
				<span className='text-[15px] font-bold text-slate-900'>{verdict}</span>
			</div>

			{/* Reasons — short bullets, not a paragraph */}
			{reasons.length > 0 && (
				<ul className='mt-2.5 space-y-1.5'>
					{reasons.map((reason, i) => (
						<li key={i} className='flex gap-2 text-sm leading-snug text-slate-700'>
							<span className={cn('mt-1.5 h-1 w-1 flex-shrink-0 rounded-full', isMis ? 'bg-purple-400' : 'bg-red-400')} />
							<span>{reason}</span>
						</li>
					))}
				</ul>
			)}

			{/* Drill-down pointer */}
			<div className='mt-2.5 text-[11px] font-medium text-slate-400'>
				For the full story, see the email conversation below ↓
			</div>
		</div>
	);
};

export const ForensicPanel: React.FC<{ forensic?: Forensic }> = ({ forensic }) => {
	if (!forensic) return null;
	const F = forensic;
	const tactics = (F.tactics || []).slice().sort((a, b) => {
		const rank = (s?: string) => ({ high: 0, medium: 1, low: 2 })[s || ''] ?? 3;
		return rank(a.sev) - rank(b.sev);
	});

	return (
		<div className='rounded-xl border border-slate-200 bg-white p-4 shadow-sm'>
			<h4 className='mb-3 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500'>
				◆ Deep review{' '}
				<span className='font-medium normal-case tracking-normal text-slate-400'>
					— full email-thread review
				</span>
			</h4>

			{F.summary && <div className='mb-3 text-sm leading-relaxed text-gray-700'>{F.summary}</div>}

			{tactics.length > 0 && (
				<>
					<div className='mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500'>
						⚠ Client pushback / delay tactics
					</div>
					<div className='space-y-2'>
						{tactics.map((t, i) => (
							<div
								key={i}
								className={cn('rounded border-l-4 p-2.5', SEV_CLASS[t.sev || 'low'] || SEV_CLASS.low)}
							>
								<div className='text-xs font-semibold text-gray-800'>
									<span className='uppercase'>{t.sev}</span> · <b>{t.by || '?'}</b> · {t.tactic}
								</div>
								{t.summary && <div className='mt-0.5 text-xs text-gray-600'>{t.summary}</div>}
								{t.quote && (
									<div className='mt-1 text-xs italic text-gray-500'>&quot;{t.quote}&quot;</div>
								)}
							</div>
						))}
					</div>
				</>
			)}

			{F.claims && F.claims.length > 0 && (
				<>
					<div className='mb-2 mt-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500'>
						Claims checked
					</div>
					<div className='space-y-2'>
						{F.claims.map((c, i) => (
							<div key={i} className='rounded border border-gray-200 bg-white p-2.5'>
								<div className='flex flex-wrap items-center gap-2 text-[11px] text-gray-500'>
									<span
										className={cn(
											'rounded px-1.5 py-0.5 font-semibold',
											CLAIM_STATUS_CLASS[c.status || ''] || 'bg-gray-100 text-gray-600'
										)}
									>
										{(c.status || '').toUpperCase()}
									</span>
									<span>
										{c.by} {c.to ? `→ ${c.to}` : ''}
									</span>
									{c.date && <span>· {String(c.date).slice(0, 16)}</span>}
								</div>
								<div className='mt-1 text-sm italic text-gray-800'>&quot;{c.verbatim}&quot;</div>
								{c.backed_by && (
									<div className='mt-1 text-xs text-gray-500'>{c.backed_by}</div>
								)}
							</div>
						))}
					</div>
				</>
			)}

			{F.notes && (
				<div className='mt-3 rounded bg-white p-2.5 text-xs text-gray-600'>
					<b>Auditor notes:</b> {F.notes}
				</div>
			)}
		</div>
	);
};

const REMIT_STATE: Record<string, { label: string; cls: string }> = {
	reconciled: { label: '✓ Zoho confirms paid', cls: 'bg-green-100 text-green-700' },
	leak: { label: '✗ leak — Zoho unpaid', cls: 'bg-red-100 text-red-700' },
	not_in_zoho: { label: '⊘ not in Zoho', cls: 'bg-amber-100 text-amber-700' },
};

export const RemittanceTable: React.FC<{ invoice: InvoiceRecord }> = ({ invoice }) => {
	const remit = invoice.remit;
	if (!remit || remit.length === 0) return null;

	const mine = remit.filter(x => x.inv === invoice.num);
	const others = remit.filter(x => x.inv !== invoice.num);
	const nLeak = remit.filter(x => x.state === 'leak').length;
	const nOk = remit.filter(x => x.state === 'reconciled').length;
	const nNF = remit.filter(x => x.state === 'not_in_zoho').length;

	const Row: React.FC<{ x: RemitLine; isThis?: boolean }> = ({ x, isThis }) => {
		const st = REMIT_STATE[x.state || ''] || { label: x.state || '—', cls: 'bg-gray-100 text-gray-600' };
		return (
			<tr className={cn('border-b border-gray-100', isThis && 'bg-blue-50/40')}>
				<td className='px-2 py-1.5 font-mono text-xs'>
					{x.invoice_pdf ? (
						<a href={x.invoice_pdf} target='_blank' rel='noopener noreferrer' className='text-blue-600 hover:underline'>
							{x.inv} 📄
						</a>
					) : (
						x.inv
					)}
					{isThis && <span className='ml-1 rounded bg-blue-100 px-1 text-[9px] text-blue-700'>this</span>}
				</td>
				<td className='px-2 py-1.5 text-xs text-gray-600'>{x.paid_date || '—'}</td>
				<td className='px-2 py-1.5 text-right text-xs'>{x.amount_text ? `₹${x.amount_text}` : '—'}</td>
				<td className='px-2 py-1.5 text-right text-xs'>
					{x.amount_delta != null && x.amount_delta !== 0 ? (
						<span className={x.amount_delta < 0 ? 'text-red-600' : 'text-green-600'}>
							{x.amount_delta < 0 ? '▼' : '▲'} {inrCompact(Math.abs(x.amount_delta))}
						</span>
					) : (
						'—'
					)}
				</td>
				<td className='px-2 py-1.5 text-xs'>
					{x.utr ? (
						<span className='font-mono text-gray-700'>{x.utr}</span>
					) : x.needs_proof ? (
						<span className='text-amber-600'>⚠ no ref</span>
					) : (
						<span className='text-gray-400'>{x.verification || '—'}</span>
					)}
				</td>
				<td className='px-2 py-1.5'>
					<span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', st.cls)}>
						{st.label}
					</span>
				</td>
				<td className='px-2 py-1.5'>
					{x.proof_url ? (
						<a href={x.proof_url} target='_blank' rel='noopener noreferrer' className='text-xs text-blue-600 hover:underline'>
							✓ proof ↗
						</a>
					) : (
						<span className='text-[10px] text-gray-400'>no doc proof</span>
					)}
				</td>
			</tr>
		);
	};

	const Head = () => (
		<thead>
			<tr className='border-b border-gray-200 bg-gray-50 text-[10px] uppercase text-gray-500'>
				<th className='px-2 py-1.5 text-left'>Invoice</th>
				<th className='px-2 py-1.5 text-left'>Client paid</th>
				<th className='px-2 py-1.5 text-right'>Client amount</th>
				<th className='px-2 py-1.5 text-right'>Variance</th>
				<th className='px-2 py-1.5 text-left'>Evidence / UTR</th>
				<th className='px-2 py-1.5 text-left'>Reconciliation</th>
				<th className='px-2 py-1.5 text-left'>Proof</th>
			</tr>
		</thead>
	);

	return (
		<div>
			<h4 className='mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400'>
				Payment reconciliation for this invoice
			</h4>
			<div className='mb-2 flex gap-3 text-xs'>
				<span className='text-green-600'>
					<b>{nOk}</b> matched
				</span>
				<span className='text-red-600'>
					<b>{nLeak}</b> leak
				</span>
				<span className='text-amber-600'>
					<b>{nNF}</b> not in Zoho
				</span>
			</div>

			{mine.length > 0 && (
				<div className='overflow-x-auto rounded-lg border border-gray-200'>
					<table className='w-full'>
						<Head />
						<tbody>
							{mine.map((x, i) => (
								<Row key={i} x={x} isThis />
							))}
						</tbody>
					</table>
				</div>
			)}

			{others.length > 0 && (
				<details className='mt-2'>
					<summary className='cursor-pointer text-xs text-gray-600'>
						▸ Other invoices in the same client remittance ({others.length}) — paid together in a bulk
						statement
					</summary>
					<div className='mt-1 overflow-x-auto rounded-lg border border-gray-200'>
						<table className='w-full'>
							<Head />
							<tbody>
								{others.map((x, i) => (
									<Row key={i} x={x} />
								))}
							</tbody>
						</table>
					</div>
				</details>
			)}
		</div>
	);
};
